// hooks/useApplication.js
import { useState, useEffect, useCallback } from 'react';
import { applicationApi, fileApi } from '../api/applicationApi';
import { mapApplicationToComponentData } from '../utils/applicationDataMapper';

export const useApplication = (applicationId) => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [statusesError, setStatusesError] = useState(null);

  const fetchApplication = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await applicationApi.getApplicationById(applicationId);
      const mappedData = mapApplicationToComponentData(data);
      setApplication(mappedData);
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка завантаження заявки');
      console.error('Error fetching application:', err);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  const fetchApplicationStatuses = useCallback(async () => {
    try {
      setStatusesLoading(true);
      setStatusesError(null);
      const data = await applicationApi.getApplicationStatuses(applicationId);
      setStatuses(data);
      return data;
    } catch (err) {
      setStatusesError(err.response?.data?.message || 'Помилка завантаження статусів');
      console.error('Error fetching application statuses:', err);
      throw err;
    } finally {
      setStatusesLoading(false);
    }
  }, [applicationId]);

  const updateApplicationStatus = useCallback(async (statusData) => {
    try {
      const updatedApplication = await applicationApi.updateApplicationStatus(applicationId, statusData);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка оновлення статусу');
      throw err;
    }
  }, [applicationId]);

  const verifyRegistration = useCallback(async (comment = '') => {
    try {
      const updatedApplication = await applicationApi.verifyRegistration(applicationId, comment);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка підтвердження реєстрації');
      throw err;
    }
  }, [applicationId]);

  const verifyBenefit = useCallback(async (benefitId, comment = '') => {
    try {
      const updatedApplication = await applicationApi.verifyBenefit(applicationId, benefitId, comment);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка підтвердження пільги');
      throw err;
    }
  }, [applicationId]);

  const rejectApplication = useCallback(async (reason, comment = '') => {
    try {
      const updatedApplication = await applicationApi.rejectApplication(applicationId, reason, comment);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка відхилення заявки');
      throw err;
    }
  }, [applicationId]);

  const restoreApplication = useCallback(async (comment = '') => {
    try {
      const updatedApplication = await applicationApi.restoreApplication(applicationId, comment);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка відновлення заявки');
      throw err;
    }
  }, [applicationId]);

  const declineInvitation = useCallback(async (reason, kindergartenId, groupNumber) => {
    try {
      const updatedApplication = await applicationApi.declineInvitation(
        applicationId, 
        reason, 
        kindergartenId, 
        groupNumber
      );
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка відхилення запрошення');
      throw err;
    }
  }, [applicationId]);

  const downloadFile = useCallback(async (doc_id, fileId, path, fileName) => {
    try {
      await fileApi.downloadFile(doc_id, fileId, path, fileName);
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка завантаження файлу');
      throw err;
    }
  }, []);

  useEffect(() => {
    if (applicationId) {
      fetchApplication();
    }
  }, [applicationId, fetchApplication]);

  return {
    application,
    loading,
    error,
    statuses,
    statusesLoading,
    statusesError,
    refetch: fetchApplication,
    fetchApplicationStatuses,
    updateApplicationStatus,
    verifyRegistration,
    verifyBenefit,
    rejectApplication,
    restoreApplication,
    declineInvitation,
    downloadFile
  };
};